import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Button,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { ptBR } from '@mui/x-data-grid/locales';

import TableService from '../../services/TableService';
import {
  ensurePaginationInUrl,
  pushPaginationToUrl,
  readPaginationFromSearch,
  resetPageOnFilterChange,
} from './tablePaginationUtils';
import {
  getTableV2BodyCellSx,
  getTableV2ExpandedMasterRowSx,
  getTableV2HeaderCellSx,
  getTableV2MasterRowSx,
  TABLE_V2_CELL_FONT_SIZE,
  TABLE_V2_ROW_HEIGHT_DEFAULT,
  tableV2AreaContentFitSx,
  tableV2ContainerSx,
  tableV2DetailPanelRowSx,
  tableV2EmptyStateSx,
  tableV2HeaderActionsBoxSx,
  tableV2HeaderButtonSx,
  tableV2LoadingBodyFitSx,
  tableV2LoadingLinearSx,
  tableV2PaginationSx,
  tableV2SkeletonCellSx,
  tableV2TableContainerFitSx,
  tableV2TableSx,
} from './tableV2Styles';

const gridLocaleText =
  ptBR.components?.MuiDataGrid?.defaultProps?.localeText ?? {};

const defaultLabelDisplayedRows = ({ from, to, count }) =>
  `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`;

const ExpandableTableV2 = ({
  columns,
  useApi = false,
  fetchUrl,
  fetchData: customFetchData,
  paginationUrl,
  filterData,
  headerActions,
  renderSubComponent,
  getRowCanExpand,
  singleExpand = true,
  rowHeight = TABLE_V2_ROW_HEIGHT_DEFAULT,
  emptyMessage = 'Nenhum registro encontrado',
  refreshKey,
  pageSize = 10,
  getRowId,
  expanded: controlledExpanded,
  onExpandedChange: controlledOnExpandedChange,
}) => {
  const history = useHistory();
  const location = useLocation();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(Boolean(useApi || customFetchData));
  const [page, setPage] = useState(0);
  const [pageSizeState, setPageSizeState] = useState(pageSize);
  const [internalExpanded, setInternalExpanded] = useState({});
  const filterDataMountRef = useRef(true);

  const isExpandedControlled = controlledExpanded !== undefined;
  const expanded = isExpandedControlled ? controlledExpanded : internalExpanded;
  const setExpanded = isExpandedControlled ? controlledOnExpandedChange : setInternalExpanded;

  const paginationBase = paginationUrl || location.pathname;
  const headerCellSx = getTableV2HeaderCellSx();
  const bodyCellSx = getTableV2BodyCellSx(rowHeight);
  const skeletonCellSx = tableV2SkeletonCellSx(rowHeight);

  useLayoutEffect(() => {
    if (!useApi && !customFetchData) return;
    const { page: p, pageSize: s } = readPaginationFromSearch(location.search, pageSize);
    setPage(p);
    setPageSizeState(s);
  }, [useApi, customFetchData, location.search, pageSize]);

  useEffect(() => {
    if (!useApi && !customFetchData) return;
    ensurePaginationInUrl(history, paginationBase, location.search, pageSize);
  }, [useApi, customFetchData, paginationBase, location.search, pageSize, history]);

  useEffect(() => {
    if (!useApi && !customFetchData) {
      filterDataMountRef.current = false;
      return;
    }
    if (filterDataMountRef.current) {
      filterDataMountRef.current = false;
      return;
    }
    resetPageOnFilterChange(
      history,
      paginationBase,
      location.search,
      pageSizeState,
      pageSize
    );
    setPage(0);
  }, [filterData]);

  const loadRows = useCallback(async () => {
    if (!useApi && !customFetchData) return;

    setLoading(true);
    try {
      if (customFetchData) {
        const result = await customFetchData(page, pageSizeState, filterData);
        setRows(result.rows || []);
        setRowCount(result.totalRows ?? 0);
      } else {
        const response = await TableService.request(
          `${fetchUrl}?p=${page}&s=${pageSizeState}`,
          filterData
        );
        setRows(response?.data?.rows || []);
        setRowCount(response?.data?.totalRows || 0);
      }
    } catch (e) {
      console.error(e);
      setRows([]);
      setRowCount(0);
    }
    setLoading(false);
  }, [useApi, customFetchData, fetchUrl, page, pageSizeState, filterData]);

  useEffect(() => {
    if (useApi || customFetchData) {
      loadRows();
    }
  }, [loadRows, refreshKey]);

  const handleExpandedChange = useCallback(
    (updater) => {
      if (!setExpanded) return;
      setExpanded((old) => {
        const prev = old || {};
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (!singleExpand) return next;

        const expandedKeys = Object.keys(next).filter((k) => next[k]);
        if (expandedKeys.length <= 1) return next;

        const newlyExpanded = expandedKeys.find((k) => !prev[k]);
        if (newlyExpanded) {
          return { [newlyExpanded]: true };
        }
        return next;
      });
    },
    [singleExpand, setExpanded]
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      expanded,
      pagination: {
        pageIndex: page,
        pageSize: pageSizeState,
      },
    },
    onExpandedChange: handleExpandedChange,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: getRowCanExpand || (() => true),
    getRowId: getRowId || ((row, index) => String(row.id ?? index)),
    manualPagination: Boolean(useApi || customFetchData),
    rowCount,
    paginateExpandedRows: false,
  });

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    setExpanded({});
    if (useApi || customFetchData) {
      pushPaginationToUrl(history, paginationBase, location.search, newPage, pageSizeState);
    }
  };

  const handleRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPageSizeState(newSize);
    setPage(0);
    setExpanded({});
    if (useApi || customFetchData) {
      pushPaginationToUrl(history, paginationBase, location.search, 0, newSize);
    }
  };

  const showEmpty = !loading && rows.length === 0;
  const showSkeleton = loading && rows.length === 0;
  const skeletonRowCount = Math.min(pageSizeState, 10);
  const visibleColumnCount = table.getVisibleLeafColumns().length || columns.length;

  const paginationLabelDisplayedRows =
    typeof gridLocaleText.MuiTablePagination?.labelDisplayedRows === 'function'
      ? gridLocaleText.MuiTablePagination.labelDisplayedRows
      : defaultLabelDisplayedRows;

  return (
    <Box sx={tableV2ContainerSx}>
      {headerActions && headerActions.length > 0 && (
        <Box sx={tableV2HeaderActionsBoxSx}>
          {headerActions.map((action, i) => (
            <Button
              key={i}
              variant={action.variant || 'contained'}
              startIcon={action.startIcon}
              onClick={action.onClick}
              size={action.size || 'medium'}
              sx={tableV2HeaderButtonSx(i)}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      )}

      <Box sx={tableV2AreaContentFitSx}>
        {loading && <LinearProgress sx={tableV2LoadingLinearSx} />}

        <Box sx={tableV2LoadingBodyFitSx(loading)}>
          <TableContainer sx={tableV2TableContainerFitSx}>
            <Table
              stickyHeader
              size="medium"
              aria-label="Tabela expansível"
              sx={tableV2TableSx}
            >
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        colSpan={header.colSpan}
                        sx={{
                          ...headerCellSx,
                          width: header.getSize() !== 150 ? header.getSize() : undefined,
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                {showSkeleton &&
                  Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                    <TableRow key={`skeleton-${rowIndex}`} hover={false}>
                      {Array.from({ length: visibleColumnCount }).map((__, cellIndex) => (
                        <TableCell key={`skeleton-cell-${cellIndex}`} sx={skeletonCellSx}>
                          <Skeleton
                            variant="text"
                            animation="wave"
                            sx={{ fontSize: TABLE_V2_CELL_FONT_SIZE }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}

                {showEmpty && (
                  <TableRow hover={false}>
                    <TableCell colSpan={visibleColumnCount} sx={tableV2EmptyStateSx}>
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                )}

                {!showEmpty &&
                  !showSkeleton &&
                  table.getRowModel().rows.map((row, rowIndex) => (
                    <React.Fragment key={row.id}>
                      <TableRow
                        hover
                        sx={{
                          ...(row.getIsExpanded()
                            ? getTableV2ExpandedMasterRowSx(getTableV2MasterRowSx(rowIndex))
                            : getTableV2MasterRowSx(rowIndex)),
                          cursor: row.getCanExpand() ? 'pointer' : 'default',
                        }}
                        onClick={() => {
                          if (row.getCanExpand()) {
                            row.toggleExpanded();
                          }
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            sx={bodyCellSx}
                            onClick={(e) => {
                              if (cell.column.id === 'expand') {
                                e.stopPropagation();
                              }
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                      {row.getIsExpanded() && renderSubComponent && (
                        <TableRow hover={false}>
                          <TableCell
                            colSpan={row.getVisibleCells().length}
                            sx={tableV2DetailPanelRowSx}
                          >
                            {renderSubComponent({ row })}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {(useApi || customFetchData) && (
            <TablePagination
              component="div"
              count={rowCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={pageSizeState}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage={
                gridLocaleText.MuiTablePagination?.labelRowsPerPage || 'Linhas por página:'
              }
              labelDisplayedRows={paginationLabelDisplayedRows}
              sx={tableV2PaginationSx}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

ExpandableTableV2.propTypes = {
  columns: PropTypes.array.isRequired,
  useApi: PropTypes.bool,
  fetchUrl: PropTypes.string,
  /** Substitui fetchUrl — assinatura: (page, pageSize, filterData) => Promise<{ rows, totalRows }> */
  fetchData: PropTypes.func,
  paginationUrl: PropTypes.string,
  filterData: PropTypes.object,
  headerActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      startIcon: PropTypes.node,
      variant: PropTypes.string,
      size: PropTypes.string,
    })
  ),
  renderSubComponent: PropTypes.func.isRequired,
  getRowCanExpand: PropTypes.func,
  singleExpand: PropTypes.bool,
  rowHeight: PropTypes.number,
  emptyMessage: PropTypes.string,
  refreshKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  pageSize: PropTypes.number,
  getRowId: PropTypes.func,
  expanded: PropTypes.object,
  onExpandedChange: PropTypes.func,
};

ExpandableTableV2.defaultProps = {
  useApi: false,
  fetchUrl: '',
  fetchData: undefined,
  paginationUrl: '',
  filterData: undefined,
  headerActions: undefined,
  getRowCanExpand: undefined,
  singleExpand: true,
  rowHeight: TABLE_V2_ROW_HEIGHT_DEFAULT,
  emptyMessage: 'Nenhum registro encontrado',
  refreshKey: undefined,
  pageSize: 10,
  getRowId: undefined,
  expanded: undefined,
  onExpandedChange: undefined,
};

export default ExpandableTableV2;
